import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const NationalityGenderGroupedStackedBars = ({ data }) => {
  // Group data by nationality and aggregate gender counts
  const groupedData = {};
  
  data.forEach(item => {
    const nationality = item.nationality || "Unknown";
    
    if (!groupedData[nationality]) {
      groupedData[nationality] = {
        nationality: nationality,
        id_: item.id_,
        gender: {
          male: 0,
          female: 0
        }
      };
    }
    
    // Aggregate gender counts
    if (item.gender && Array.isArray(item.gender)) {
      item.gender.forEach(g => {
        if (g.name === "male") {
          groupedData[nationality].gender.male += g.count || 0;
        } else if (g.name === "female") {
          groupedData[nationality].gender.female += g.count || 0;
        }
      });
    }
  });
  
  // Convert grouped data to arrays
  const groupedArray = Object.values(groupedData);
  
  // Prepare the data for the chart
  const nationalities = groupedArray.map(item => item.nationality);
  const maleCounts = groupedArray.map(item => item.gender.male || 0);
  const femaleCounts = groupedArray.map(item => item.gender.female || 0);

  const chartRef = useRef(null);

  // Handle chart click events
  const handleClick = (event) => {
    const points = chartRef.current.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    );

    if (points.length) {
      const firstPoint = points[0];
      const datasetIndex = firstPoint.datasetIndex;
      const index = firstPoint.index;
      
      // Get nationality and gender
      const selectedNationality = groupedArray[index];
      const gender = datasetIndex === 0 ? 'male' : 'female';
      
      // Open employees page in new tab with filters
      window.open(`/employees?nationality=${selectedNationality?.id_}&gender=${gender}`, '_blank');
    }
  };

  // Plugin to render data values inside the bars
  const datalabelsPlugin = {
    id: 'datalabels',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      
      ctx.save();
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      data.datasets.forEach((dataset, datasetIndex) => {
        // Skip if dataset is hidden
        if (chart.getDatasetMeta(datasetIndex).hidden) {
          return;
        }
        
        const meta = chart.getDatasetMeta(datasetIndex);
        
        meta.data.forEach((bar, index) => {
          const value = dataset.data[index];
          // Only show value if it's a positive number and the bar is visible
          if (value > 0 && !meta.data[index].hidden) {
            // Calculate position
            const xPos = bar.x;
            const yPos = datasetIndex === 0 ? 
              bar.y + (bar.height / 2) : // For male (top bars)
              bar.y + (bar.height / 2);  // For female (bottom bars)
            
            // Draw white text directly without background
            ctx.fillStyle = '#fff'; // White text
            ctx.fillText(value.toString(), xPos, yPos);
          }
        });
      });
      
      ctx.restore();
    }
  };

  const chartData = {
    labels: nationalities,
    datasets: [
      {
        label: 'Male',
        data: maleCounts.map(count => count === 0 ? null : count),
        backgroundColor: '#D3001A',
        borderRadius: 4,
        borderWidth: 0,
        stack: 'Stack 0',
      },
      {
        label: 'Female',
        data: femaleCounts.map(count => count === 0 ? null : count),
        backgroundColor: '#ffb500',
        borderRadius: 4,
        borderWidth: 0,
        stack: 'Stack 0',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'center',
      },
      // Disable any built-in data labels
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          afterBody: (tooltipItems) => {
            const idx = tooltipItems[0].dataIndex;
            const male = maleCounts[idx];
            const female = femaleCounts[idx];
            const total = male + female;
            return `Total: ${total}`;
          },
        },
        backgroundColor: 'white',
        titleColor: '#333',
        bodyColor: '#333',
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.1)',
        displayColors: true,
        padding: 10,
        bodyFont: {
          weight: 'bold',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Employee Count',
        },
        min: 0,
        ticks: {
          precision: 0,
          stepSize: 1
        }
      },
    },
    barPercentage: 0.8,
    minBarLength: 15,
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement.length ? 'pointer' : 'default';
    }
  };

  const minChartWidth = Math.max(groupedArray.length * 40, 300); // At least 40px per bar, minimum 300px


  return (
    <div style={{ 
      overflowX: 'auto', // Enable horizontal scrolling
      width: '100%'      // Use full container width
    }}>
      <div style={{ 
        height: "290px", 
        minWidth: `${minChartWidth}px`, // Ensure chart has room for all bars
        // cursor: 'pointer' // Change cursor to indicate clickable elements
      }}>
      <Bar 
        ref={chartRef} 
        options={options} 
        data={chartData} 
        plugins={[datalabelsPlugin]} 
        onClick={handleClick}
      />
      </div>
    </div>
  );
};

export default NationalityGenderGroupedStackedBars;
